<template>
    <div>
        <div class="card">
            <div class="card-header bg-primary text-white">
                <div class="card-title h5 m-0">{{$t('certificates.title')}}</div>
            </div>

            <div class="btn-toolbar justify-content-between mb-0 mt-3" role="toolbar">
                <div class="col-w"></div>
                <div class="input-group">
                    <input v-model="searchQuery"
                           v-on:keyup.enter.stop="massSearch"
                           v-on:keyup.esc.stop="clearFilters"
                           type="text"
                           class="form-control"
                           aria-label="Mass search input"
                           aria-describedby="massSearch" />
                    <button class="btn btn-secondary" @click.stop="massSearch">
                        <i class="fas fa-search"></i>
                        {{ $t('buttons.search.btnText') }}
                    </button>
                    <button class="btn btn-secondary" @click.stop="clearFilters">
                        <i class="fas fa-times"></i>
                        {{ $t('buttons.clearFilters.btnText') }}
                    </button>
                </div>
            </div>

            <div class="loader" v-show="loading"></div>
            <div class="card-body" v-show="!loading">
                <div class="grid-wrapper">
                    <kendo-datasource ref="datasource"
                                      :transport="transportSettings"
                                      :schema-model-fields="schemaModelFields"
                                      :schema-model-id="'id'"
                                      :schema-total="'total'"
                                      :schema-data="'data'"
                                      :page-size="defaultGridRows"
                                      :server-paging="true"
                                      :server-sorting="true"
                                      :server-filtering="true"
                                      :change="onChange"></kendo-datasource>

                    <kendo-grid ref="certificatesGrid"
                                :data-source-ref="'datasource'"
                                :editable="gridOptions.editable"
                                :filterable="gridOptions.filterable"
                                :pageable="gridOptions.pageable"
                                :reorderable="gridOptions.reorderable"
                                :resizable="gridOptions.resizable"
                                :groupable="gridOptions.groupable"
                                :allow-copy="gridOptions.allowCopy"
                                :column-menu="gridOptions.columnMenu"
                                :sortable-mode="gridOptions.sortable.mode"
                                :sortable-allow-unsort="gridOptions.sortable.allowUnsort"
                                :sortable-show-indexes="gridOptions.sortable.showIndexes"
                                :selectable="gridOptions.selectable"
                                :scrollable="gridOptions.scrollable"
                                :dataBound="onDatabound">
                        <kendo-grid-column :template="btnsTemplate"
                                           :width="80"
                                           field="btnsColumn"
                                           title=" ">
                        </kendo-grid-column>
                        <kendo-grid-column :field="'id'" :title="'Id'"></kendo-grid-column>
                        <kendo-grid-column :field="'number'" :title="$t('certificates.number')"></kendo-grid-column>
                        <kendo-grid-column :field="'personName'" :title="$t('certificates.personName')"></kendo-grid-column>
                        <kendo-grid-column :field="'eventId'" :title="$t('certificates.eventId')"></kendo-grid-column>
                        <kendo-grid-column :field="'eventName'" :title="$t('certificates.eventName')"></kendo-grid-column>
                        <kendo-grid-column :field="'eventCode'" :title="$t('certificates.eventCode')"></kendo-grid-column>
                        <kendo-grid-column :field="'evaluation'" :title="$t('certificates.evaluation')" :hidden="true"></kendo-grid-column>
                        <kendo-grid-column :field="'createdByUsername'" :title="$t('certificates.createdBy')"></kendo-grid-column>
                        <kendo-grid-column :field="'createdOn'" :title="$t('certificates.createdOn')" :format="customDateTimeFormat"></kendo-grid-column>
                        <kendo-grid-column :field="'hasSigningProcedure'" :title="$tc('signing.pageTitle', 1)" :template="hasSigningProcedureCellTemplate"></kendo-grid-column>
                        <kendo-grid-column :field="'signedOn'" :title="$t('signing.filesGirdHeaders.timestampUtc')" :format="customDateTimeFormat"></kendo-grid-column>
                        <kendo-grid-column :field="'signedByUsername'" :title="$t('signing.filesGirdHeaders.signedBy')"></kendo-grid-column>
                    </kendo-grid>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import Vue from 'vue';
    import BtnsTemplate from '@/components/grid/StandardBtnsTemplate';
    import { mapGetters } from 'vuex'

    export default {
        name: 'CertificatesListView',
        data() {
            return {
                transportSettings: {
                    read: {
                        url: this.$store.getters.baseUrl + '/api/certificates/all',
                        dataType: 'json',
                        type: 'POST',
                        contentType: 'application/json',
                        beforeSend: this.beforeSend
                    },
                    parameterMap: this.parameterMap
                },
                defaultGridRows: this.$store.getters.defaultGridRows,
                searchQuery: '',
                schemaModelFields: {
                    id: { field: 'id', type: 'number', nullable: false, editable: false },
                    number: { field: 'number', type: 'string', nullable: true, editable: false },
                    personId: { field: 'personId', type: 'string', nullable: true, editable: false },
                    personName: { field: 'personName', type: 'string', nullable: true, editable: false },
                    eventId: { field: 'eventId', type: 'string', nullable: true, editable: false },
                    eventName: { field: 'eventName', type: 'string', nullable: true, editable: false },
                    eventCode: { field: 'eventCode', type: 'string', nullable: true, editable: false },
                    validFrom: { field: 'validFrom', type: 'date', nullable: true, editable: false },
                    validTo: { field: 'validTo', type: 'date', nullable: true, editable: false },
                    evaluation: { field: 'evaluation', type: 'string', nullable: true, editable: false },
                    attachmentId: { field: 'attachmentId', type: 'string', nullable: true, editable: false },
                    createdOn: { field: 'createdOn', type: 'date', nullable: true, editable: false },
                    createdBy: { field: 'createdBy', type: 'string', nullable: true, editable: false },
                    createdByUsername: { field: 'createdByUsername', type: 'string', nullable: true, editable: false },
                    deletedOn: { field: 'deletedOn', type: 'date', nullable: true, editable: false },
                    deletedBy: { field: 'deletedBy', type: 'string', nullable: true, editable: false },
                    deletedByUsername: { field: 'deletedByUsername', type: 'string', nullable: true, editable: false },
                    docTypeCode: { field: 'docTypeCode', type: 'string', nullable: true, editable: false },
                    hash: { field: 'hash', type: 'string', nullable: true, editable: false },
                    title: { field: 'title', type: 'string', nullable: true, editable: false },
                    hasSigningProcedure: { field: 'hasSigningProcedure', type: 'boolean', nullable: true, editable: false },
                    signingId: { field: 'signingId', type: 'number', nullable: true, editable: false },
                    signedOn: { field: 'signedOn', type: 'date', nullable: true, editable: false },
                    signedByUsername: { field: 'signedByUsername', type: 'string', nullable: true, editable: false },
                },
                loading: false
            };
        },
        methods: {
            onDatabound(e) {
                var items = e.sender.items();
                items.each(function (idx, item) {
                    let dataItem = e.sender.dataItem(item);
                    if (dataItem.statusId === 2) {
                        $(item).addClass('left-border-green');
                    } else if (dataItem.statusId === 4 || dataItem.statusId === 7) {
                        $(item).addClass('left-border-blue');
                    }
                });
            },
            parameterMap: function (options, operation) {
                if (operation !== 'read' && options.models) {
                    return { models: kendo.stringify(options.models) };
                } else {
                    return kendo.stringify(options);
                }
            },
            onChange(data) {
                // parse date field in the grid data
                data.items.forEach(item => {
                    item.validFrom = item.validFrom ? new Date(item.validFrom) : '';
                    item.validTo = item.validTo ? new Date(item.validTo) : '';
                    item.createdOn = item.createdOn ? new Date(item.createdOn) : '';
                });
            },
            beforeSend: function (req) {
                if (this.authToken) {
                    req.setRequestHeader('Authorization', 'Bearer ' + this.authToken);
                }
            },
            boolCellTemplate: function (dataItem) {
                let templateHtml =
                    '<div class="text-center"><span class="badge ' +
                    (dataItem.archived ? 'badge-success' : 'badge-secondary') +
                    '">' +
                    kendo.htmlEncode(dataItem.archived ? this.$t('yes') : this.$t('no')) +
                    '</span></div>';

                return templateHtml;
            },
            hasSigningProcedureCellTemplate(dataItem) {
                let templateHtml =
                    '<span class="badge ' + (dataItem.hasSigningProcedure ? 'badge-success' : 'badge-light') + '">' + kendo.htmlEncode(dataItem.hasSigningProcedure ? this.$t('yes') : this.$t('no')) + '</span>';

                return templateHtml;
            },
            btnsTemplate: function (e) {
                return {
                    template: Vue.component(BtnsTemplate.name, BtnsTemplate),
                    templateArgs: {
                        ...e,
                        // parentComponent: this.$refs.createdByMeGrid,
                        btns: [
                            {
                                name: 'download',
                                text: '',
                                tooltip: this.$t('certificates.download'),
                                class: 'btn btn-info',
                                icon: 'far fa-file-pdf',
                                show: true,
                                clickHandler: this.downloadPdf
                            },
                            {
                                name: 'delete',
                                text: '',
                                tooltip: this.$t('certificates.delete'),
                                class: 'btn btn-danger',
                                icon: 'far fa-trash-alt',
                                show: this.hasDeleteRights,
                                clickHandler: this.deleteCertificate
                            },
                            {
                                name: 'signingDetails',
                                text: '',
                                tooltip: this.$tc('signing.pageTitle', 1),
                                class: 'btn btn-info',
                                icon: 'fas fa-eye',
                                show: e.signingId && this.hasSigningReadRights,
                                clickHandler: this.signingDetailsBtnClick
						},
                        ]
                    }
                };
            },
            downloadPdf(e) {
                const downloadUri = this.$store.getters.baseUrl + `/api/file/download/?id=${e.attachmentId}&type=${e.docTypeCode}&hash=${e.hash}`;
                this.loading = true;

                this.$http.get(downloadUri, { responseType: 'blob' })
                    .then(response => {
                        const blob = new Blob([response.data]);
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', e.title); // or any other extension
                        document.body.appendChild(link);
                        link.click();
                        window.URL.revokeObjectURL(url);
                    })
                    .catch(err => {
                        this.$vs.notify({
                            color: 'danger',
                            title: this.$t('notifications.error'),
                            text: this.$helpers.errorText(err)
                        })
                    })
                    .then(() => { this.loading = false; });
            },
            deleteCertificate(args) {
                if (!this.hasDeleteRights) {
                    return this.$router.push(`/unauthorized`);
                }

                kendo.confirm(`${this.$t('certificates.deletionMessage')}`)
                    .then(function () {
                        this.doDelete(args);
                    }.bind(this));
            },
            doDelete(args) {
                this.$http.delete('/api/certificates/' + args.id)
                    .then(response => {
                        this.$vs.notify({
                            color: 'success',
                            title: this.$t('updateSuccess'),
                            text: ''
                        });
                        this.refresh();
                    }).catch(error => {
                        this.$vs.notify({
                            color: 'danger',
                            title: this.$t('notifications.error'),
                            text: this.$helpers.errorText(err)
                        });
                    });
            },
            refresh() {
                this.$refs['certificatesGrid'].kendoWidget().dataSource.read();
            },
            clearFilters() {
                this.searchQuery = '';
                const grid = this.$refs.certificatesGrid.kendoWidget();
                if (grid) {
                    grid.dataSource.sort({});
                    grid.dataSource.page(1);
                    grid.dataSource.aggregate([]);
                    grid.dataSource.filter({});
                }
            },
            massSearch() {
                if (!this.searchQuery) {
                    this.$vs.notify({
                        title: this.$t('buttons.search.btnText'),
                        text: this.$t('emptySearchFilter'),
                        color: 'warning',
                        time: this.$store.getters.notificationDuration
                    });

                    return;
                }

                let grid = this.$refs.certificatesGrid.kendoWidget();
                if (grid) {
                    let filter = this.$helpers.getMassFilterForGrid(grid, this.searchQuery);
                    if (filter) {
                        grid.dataSource.filter(filter);
                    }
                }
            },
            signingDetailsBtnClick(e) {
                if (!this.hasSigningReadRights) return;

                let routeData = this.$router.resolve({ name: 'signatures', query: { view: e.signingId } })
                window.open(routeData.href, '_blank')
            }
        },
        computed: {
            ...mapGetters(['gridOptions', 'dateTimeFormat', 'authToken', 'claims']),
            hasDeleteRights() {
                return this.claims.certificateAdministration.delete;
            },
            hasSigningReadRights() {
                return this.claims.tasks.read;
            },
            customDateTimeFormat() {
                return `{0: ${this.dateTimeFormat}}`;
            },
        }
    };
</script>

<style>
</style>
